import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useParams, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Star, MapPin, Phone, Mail, Globe, CheckCircle, Shield,
  Award, Users, Package, Building2, ChevronRight, Send,
  ArrowLeft, Linkedin, ExternalLink, Clock, TrendingUp,
  Flag, Tag, Layers, BadgeCheck, Truck, Loader2
} from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* ─── MOCK DATA ─────────────────────────────────────────── */
const SUPPLIER_DATA = {
  slug: 'brandwear-uniforms-ltd',
  name: 'BrandWear Uniforms Ltd',
  tagline: 'Premium Corporate & Exhibition Staff Uniforms',
  coverImage: 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=2200&auto=format&fit=crop',
  verified: true,
  premium: true,
  memberSince: '2012',
  rating: 4.8,
  reviewCount: 94,
  totalOrders: 4200,
  activeClients: 78,
  responseTime: '< 6 hrs',
  location: 'Istanbul, Türkiye',
  email: 'sales@brandwearuniforms.com',
  phone: '+90 212 555 0880',
  website: 'www.brandwearuniforms.com',
  about: `BrandWear Uniforms Ltd is a leading manufacturer and supplier of custom corporate uniforms, branded workwear, and exhibition staff apparel. Headquartered in Istanbul with distribution hubs in London and Dubai, we have been dressing trade show teams, exhibition staff, and corporate personnel with precision and quality since 2012.

Our in-house design studio works closely with clients to develop bespoke uniform collections that perfectly reflect brand identity while ensuring comfort, durability, and consistent presentation across events. From polo shirts and blazers to custom aprons and branded accessories, we provide full wardrobe solutions tailored to every exhibition and corporate event environment.

We operate ISO 9001:2015 certified manufacturing facilities and proudly offer OEKO-TEX Standard 100 certified fabrics across our premium range. With fast turnaround times (as low as 7 business days for standard orders) and competitive MOQ requirements starting at just 50 units, we serve exhibition organizers, stand contractors, and corporate events teams in 18+ countries worldwide.`,
  stats: [
    { label: 'Products', value: '280+', icon: Package },
    { label: 'Countries Shipped', value: '18', icon: Globe },
    { label: 'Orders Completed', value: '4,200+', icon: TrendingUp },
    { label: 'Years in Business', value: '12', icon: Clock },
  ],
  products: [
    {
      id: 1, category: 'Exhibition Staff Uniforms',
      name: 'ProStaff Exhibition Polo', sku: 'PSP-100',
      description: 'Premium dry-fit polo shirt designed for exhibition and trade show staff. Available in 40+ colors, embroidery or print branding, moisture-wicking fabric.',
      moq: '50 pcs', leadTime: '7-10 days', price: 'From $18/pc',
      image: 'https://images.unsplash.com/photo-1594938298603-c8148c4b4357?q=80&w=600&auto=format&fit=crop',
      tags: ['Dry-Fit', 'Embroidery', 'Custom Color'],
    },
    {
      id: 2, category: 'Corporate Blazers & Jackets',
      name: 'Executive Event Blazer', sku: 'EEB-200',
      description: 'Tailored corporate blazer for premium event and exhibition staff. Available in men\'s and women\'s cuts, custom lining, internal logo label.',
      moq: '30 pcs', leadTime: '14-18 days', price: 'From $85/pc',
      image: 'https://images.unsplash.com/photo-1507679799987-c73779587ccf?q=80&w=600&auto=format&fit=crop',
      tags: ['Tailored', 'Premium', 'Custom Lining'],
    },
    {
      id: 3, category: 'Custom Polo & T-Shirts',
      name: 'BrandTee 180GSM Unisex T-Shirt', sku: 'BT-180',
      description: '180 GSM combed cotton unisex t-shirt for promoters and event staff. Full-color front and back print available. Available XS–4XL.',
      moq: '100 pcs', leadTime: '5-7 days', price: 'From $9/pc',
      image: 'https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?q=80&w=600&auto=format&fit=crop',
      tags: ['Cotton', 'Full-Color Print', 'Unisex'],
    },
    {
      id: 4, category: 'Branded Aprons & Vests',
      name: 'ProChef Exhibition Apron', sku: 'PCA-50',
      description: 'Durable canvas apron ideal for food exhibition staff, bar staff, and hospitality teams. Front pocket, adjustable neck strap, screen-print branding.',
      moq: '50 pcs', leadTime: '7-10 days', price: 'From $22/pc',
      image: 'https://images.unsplash.com/photo-1556909114-f6e7ad7d3136?q=80&w=600&auto=format&fit=crop',
      tags: ['Canvas', 'Hospitality', 'Adjustable'],
    },
    {
      id: 5, category: 'Promotional Accessories',
      name: 'Event Staff Cap — 6-Panel', sku: 'ESC-60',
      description: 'Structured 6-panel cap with embroidered logo. Available in 25+ colors. Velcro or strapback closure. One size fits most.',
      moq: '50 pcs', leadTime: '5-7 days', price: 'From $12/pc',
      image: 'https://images.unsplash.com/photo-1588850561407-ed78c282e89b?q=80&w=600&auto=format&fit=crop',
      tags: ['Cap', 'Embroidery', '6-Panel'],
    },
    {
      id: 6, category: 'Promotional Accessories',
      name: 'BrandLanyard Premium Woven', sku: 'BLP-10',
      description: 'Woven polyester lanyard with breakaway clip and custom woven branding. 15mm or 20mm width. Badge clip or hook end.',
      moq: '200 pcs', leadTime: '5-7 days', price: 'From $2.50/pc',
      image: 'https://images.unsplash.com/photo-1551816230-ef5deaed4a26?q=80&w=600&auto=format&fit=crop',
      tags: ['Woven', 'Breakaway Clip', 'Custom Logo'],
    },
  ],
  certifications: [
    { name: 'OEKO-TEX Standard 100', issuer: 'OEKO-TEX Association', year: 2023, icon: BadgeCheck },
    { name: 'ISO 9001:2015', issuer: 'TÜV Rheinland', year: 2022, icon: BadgeCheck },
    { name: 'BSCI Compliance', issuer: 'Business Social Compliance Initiative', year: 2023, icon: Award },
    { name: 'GOTS Certified', issuer: 'Global Organic Textile Standard', year: 2022, icon: BadgeCheck },
    { name: 'SEDEX Member', issuer: 'Supplier Ethical Data Exchange', year: 2021, icon: Award },
  ],
  team: [
    { name: 'Selin Yılmaz', role: 'CEO & Founder', bio: 'With 18 years in corporate apparel, Selin founded BrandWear in 2012 after managing uniform programs for major international exhibition companies.', avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b7c4?q=80&w=400&auto=format&fit=crop&crop=face' },
    { name: 'Mehmet Çelik', role: 'Head of Design', bio: 'Fashion & textile design expert with 12 years of experience. Mehmet ensures every uniform perfectly reflects the client\'s brand identity.', avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?q=80&w=400&auto=format&fit=crop&crop=face' },
    { name: 'Sarah Thompson', role: 'Sales Manager – Europe & ME', bio: 'Manages key accounts across Europe and the Middle East, coordinating large-scale uniform orders for major trade shows and events.', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=400&auto=format&fit=crop&crop=face' },
  ],
  coverage: {
    countries: [
      { flag: '🇹🇷', name: 'Türkiye', cities: ['Istanbul', 'Ankara', 'Izmir', 'Antalya'], primary: true },
      { flag: '🇬🇧', name: 'United Kingdom', cities: ['London', 'Birmingham', 'Manchester'] },
      { flag: '🇩🇪', name: 'Germany', cities: ['Frankfurt', 'Dusseldorf', 'Munich', 'Berlin'] },
      { flag: '🇦🇪', name: 'UAE', cities: ['Dubai', 'Abu Dhabi'] },
      { flag: '🇸🇦', name: 'Saudi Arabia', cities: ['Riyadh', 'Jeddah'] },
      { flag: '🇳🇱', name: 'Netherlands', cities: ['Amsterdam', 'Rotterdam'] },
      { flag: '🇫🇷', name: 'France', cities: ['Paris', 'Lyon', 'Cannes'] },
      { flag: '🇺🇸', name: 'USA', cities: ['Las Vegas', 'Chicago', 'New York'] },
    ],
    shippingNote: 'Express delivery available across Europe (2-3 business days). International orders to Middle East and Asia in 5-8 business days. Free express shipping on bulk orders over 500 units.'
  },
  reviews: [
    { id: 1, author: 'Ahmet Kaya', company: 'ExpoBuilders Istanbul', rating: 5, date: 'Jan 2026', avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?q=80&w=100&auto=format&fit=crop&crop=face', text: "BrandWear is our go-to uniform supplier for all major exhibitions. The quality is outstanding — our staff at Hannover Messe looked incredibly professional. Fast delivery and the embroidery detail is flawless. Highly recommend!" },
    { id: 2, author: 'Sophie Laurent', company: 'Paris Expo Events', rating: 5, date: 'Dec 2025', avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?q=80&w=100&auto=format&fit=crop&crop=face', text: "Excellent supplier for corporate event uniforms. We ordered blazers and polo shirts for 150 staff members at a major Paris trade show. Colors were exact matches to our brand guidelines and delivery was 2 days ahead of schedule." },
    { id: 3, author: 'Marcus Brandt', company: 'DWTC Event Services', rating: 5, date: 'Nov 2025', avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?q=80&w=100&auto=format&fit=crop&crop=face', text: "We've been using BrandWear for our Dubai exhibition staff uniforms for 3 years. Consistent quality, competitive pricing, and they always accommodate last-minute additions. Their OEKO-TEX certification is also important for our sustainability commitments." },
  ],
  ratingBreakdown: { 5: 82, 4: 10, 3: 2, 2: 0, 1: 0 },
};

const TABS = [
  { id: 'overview',        label: 'Overview' },
  { id: 'products',        label: 'Products' },
  { id: 'certifications',  label: 'Certifications' },
  { id: 'team',            label: 'Team' },
  { id: 'coverage',        label: 'Coverage & Shipping' },
  { id: 'reviews',         label: 'Reviews' },
  { id: 'contact',         label: 'Get a Quote' },
];

const Stars = ({ rating, size = 'sm' }) => {
  const sz = size === 'sm' ? 'w-4 h-4' : 'w-5 h-5';
  return (
    <div className="flex gap-0.5">
      {[1,2,3,4,5].map(s => (
        <Star key={s} className={`${sz} ${s <= Math.round(rating) ? 'fill-amber-400 text-amber-400' : 'text-gray-300'}`} />
      ))}
    </div>
  );
};

/* ─── OVERVIEW ─────────────────────────────────────────── */
const OverviewTab = ({ d }) => (
  <div className="space-y-8">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {d.stats.map((s, i) => {
        const Icon = s.icon;
        return (
          <div key={i} className="bg-white rounded-2xl p-5 border border-gray-100 shadow-sm text-center hover:shadow-md transition-shadow">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center mx-auto mb-3">
              <Icon className="w-5 h-5 text-[#0B1E4D]" />
            </div>
            <p className="text-2xl font-black text-gray-900">{s.value}</p>
            <p className="text-xs text-gray-500 mt-0.5">{s.label}</p>
          </div>
        );
      })}
    </div>

    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-8">
      <h3 className="text-xl font-bold text-gray-900 mb-4">About Us</h3>
      <div className="space-y-4">
        {d.about.split('\n\n').map((para, i) => (
          <p key={i} className="text-gray-600 leading-relaxed text-[15px]">{para}</p>
        ))}
      </div>
    </div>

    {/* Featured Products Preview */}
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-xl font-bold text-gray-900">Featured Products</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {d.products.slice(0, 3).map(p => (
          <div key={p.id} className="group bg-white border border-gray-100 rounded-xl overflow-hidden shadow-sm hover:shadow-lg transition-all hover:-translate-y-0.5">
            <div className="h-36 overflow-hidden">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-3">
              <span className="text-xs text-[#0B1E4D] font-semibold bg-blue-50 px-2 py-0.5 rounded-full">{p.category}</span>
              <p className="font-bold text-gray-900 text-sm mt-1">{p.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">MOQ: {p.moq} · {p.leadTime}</p>
              <p className="text-sm font-bold text-[#0B1E4D] mt-1">{p.price}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  </div>
);

/* ─── PRODUCTS ─────────────────────────────────────────── */
const ProductsTab = ({ d }) => {
  const cats = ['All', ...new Set(d.products.map(p => p.category))];
  const [filter, setFilter] = useState('All');
  const filtered = filter === 'All' ? d.products : d.products.filter(p => p.category === filter);
  return (
    <div className="space-y-6">
      <div className="flex gap-2 flex-wrap">
        {cats.map(c => (
          <button key={c} onClick={() => setFilter(c)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium border transition-all ${filter === c ? 'bg-[#0B1E4D] text-white border-[#0B1E4D]' : 'border-gray-200 text-gray-600 hover:border-[#0B1E4D]/30'}`}>
            {c}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        {filtered.map(p => (
          <div key={p.id} className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all hover:-translate-y-1 group">
            <div className="relative h-40 overflow-hidden">
              <img src={p.image} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
              <div className="absolute top-3 left-3">
                <span className="bg-[#0B1E4D]/90 text-white text-xs px-2.5 py-1 rounded-full font-medium">{p.category}</span>
              </div>
              <div className="absolute top-3 right-3">
                <span className="bg-white/90 text-gray-700 text-xs px-2.5 py-1 rounded-full font-mono">{p.sku}</span>
              </div>
            </div>
            <div className="p-4">
              <h4 className="font-bold text-gray-900 mb-1">{p.name}</h4>
              <p className="text-xs text-gray-500 leading-relaxed mb-3">{p.description}</p>
              <div className="flex flex-wrap gap-1.5 mb-3">
                {p.tags.map(t => (
                  <span key={t} className="bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{t}</span>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 border-t border-gray-100 pt-3">
                <div><span className="font-semibold">MOQ:</span> {p.moq}</div>
                <div><span className="font-semibold">Lead time:</span> {p.leadTime}</div>
              </div>
              <p className="text-base font-black text-[#0B1E4D] mt-2">{p.price}</p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── CERTIFICATIONS ────────────────────────────────────── */
const CertificationsTab = ({ d }) => (
  <div className="space-y-6">
    <p className="text-gray-500 text-sm max-w-xl">All certificates are current and verified. Full documentation available upon request.</p>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {d.certifications.map((c, i) => {
        const Icon = c.icon;
        return (
          <div key={i} className="bg-white border border-gray-100 rounded-2xl p-5 shadow-sm flex items-start gap-4 hover:shadow-md transition-shadow">
            <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center flex-shrink-0">
              <Icon className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="font-bold text-gray-900 text-sm">{c.name}</p>
              <p className="text-xs text-gray-500 mt-0.5">{c.issuer}</p>
              <span className="inline-block mt-2 bg-emerald-50 text-emerald-700 text-xs font-semibold px-2.5 py-0.5 rounded-full border border-emerald-100">
                Valid since {c.year}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/* ─── TEAM ──────────────────────────────────────────────── */
const TeamTab = ({ d }) => (
  <div className="space-y-6">
    <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
      {d.team.map((m, i) => (
        <div key={i} className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm hover:shadow-lg transition-all hover:-translate-y-1 text-center group">
          <div className="relative h-48 overflow-hidden">
            <img src={m.avatar} alt={m.name} className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500" />
          </div>
          <div className="p-4">
            <h4 className="font-bold text-gray-900">{m.name}</h4>
            <p className="text-xs text-[#0B1E4D] font-semibold mb-2">{m.role}</p>
            <p className="text-xs text-gray-500 leading-relaxed">{m.bio}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
);

/* ─── COVERAGE ──────────────────────────────────────────── */
const CoverageTab = ({ d }) => (
  <div className="space-y-6">
    <div>
      <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
        <Globe className="w-5 h-5 text-[#0B1E4D]" /> Delivery Countries & Cities
      </h3>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
        {d.coverage.countries.map((c, i) => (
          <div key={i} className={`rounded-2xl p-4 border ${c.primary ? 'bg-[#0B1E4D] border-[#0B1E4D] text-white' : 'bg-white border-gray-100 shadow-sm hover:shadow-md transition-shadow'}`}>
            <div className="flex items-center gap-2 mb-3">
              <span className="text-2xl">{c.flag}</span>
              <div>
                <p className={`font-bold text-sm ${c.primary ? 'text-white' : 'text-gray-900'}`}>{c.name}</p>
                {c.primary && <span className="text-xs bg-orange-500 text-white px-2 py-0.5 rounded-full">HQ</span>}
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {c.cities && c.cities.map(city => (
                <span key={city} className={`text-xs px-2 py-0.5 rounded-full border ${c.primary ? 'border-white/30 bg-white/15 text-white/90' : 'border-gray-200 text-gray-600 bg-gray-50'}`}>{city}</span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>

    {/* Active Trade Shows / Fairs Supplied */}
    {d.active_shows && d.active_shows.length > 0 && (
      <div>
        <h3 className="font-bold text-gray-900 text-lg mb-4 flex items-center gap-2">
          <Award className="w-5 h-5 text-orange-500" /> Supplied at These Trade Shows
        </h3>
        <div className="bg-gradient-to-r from-[#0B1E4D] to-[#163A7D] rounded-2xl p-6 text-white">
          <div className="flex flex-wrap gap-2 mb-4">
            {d.active_shows.map(show => (
              <Link key={show}
                to={`/find-suppliers/fair/${encodeURIComponent(show.toLowerCase().replace(/\s+/g, '-'))}`}
                className="bg-white/15 hover:bg-white/25 border border-white/20 px-3 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-1.5"
              >
                ⭐ {show}
              </Link>
            ))}
          </div>
          <p className="text-blue-200 text-xs">Click a trade show to find other suppliers active at that event</p>
        </div>
      </div>
    )}

    <div className="bg-blue-50 border border-blue-100 rounded-2xl p-5 flex items-start gap-3">
      <Truck className="w-5 h-5 text-[#0B1E4D] flex-shrink-0 mt-0.5" />
      <div>
        <p className="font-semibold text-gray-900 mb-1">Shipping Information</p>
        <p className="text-sm text-gray-600">{d.coverage.shippingNote}</p>
      </div>
    </div>
  </div>
);

/* ─── REVIEWS ───────────────────────────────────────────── */
const ReviewsTab = ({ d }) => {
  const total = Object.values(d.ratingBreakdown).reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-6">
      <div className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6 flex flex-col sm:flex-row gap-8">
        <div className="text-center sm:border-r sm:border-gray-100 sm:pr-8 flex-shrink-0">
          <p className="text-6xl font-black text-gray-900">{d.rating}</p>
          <Stars rating={d.rating} size="lg" />
          <p className="text-sm text-gray-400 mt-2">{d.reviewCount} reviews</p>
        </div>
        <div className="flex-1 space-y-2">
          {[5,4,3,2,1].map(star => {
            const count = d.ratingBreakdown[star] || 0;
            const pct = total ? Math.round((count / total) * 100) : 0;
            return (
              <div key={star} className="flex items-center gap-3">
                <div className="flex items-center gap-1 w-8">
                  <span className="text-sm text-gray-600">{star}</span>
                  <Star className="w-3 h-3 fill-amber-400 text-amber-400" />
                </div>
                <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className="h-full bg-amber-400 rounded-full" style={{ width: `${pct}%` }} />
                </div>
                <span className="text-xs text-gray-400 w-8 text-right">{count}</span>
              </div>
            );
          })}
        </div>
      </div>
      <div className="space-y-4">
        {d.reviews.map(r => (
          <div key={r.id} className="bg-white border border-gray-100 rounded-2xl shadow-sm p-6">
            <div className="flex items-start justify-between mb-3">
              <div className="flex items-center gap-3">
                <img src={r.avatar} alt={r.author} className="w-10 h-10 rounded-full object-cover" />
                <div>
                  <p className="font-bold text-gray-900 text-sm">{r.author}</p>
                  <p className="text-xs text-gray-500">{r.company}</p>
                </div>
              </div>
              <div className="text-right">
                <Stars rating={r.rating} />
                <p className="text-xs text-gray-400 mt-1">{r.date}</p>
              </div>
            </div>
            <p className="text-gray-600 text-sm leading-relaxed italic">"{r.text}"</p>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── CONTACT / QUOTE ───────────────────────────────────── */
const ContactTab = ({ d }) => {
  const { slug } = useParams();
  const [quoteForm, setQuoteForm] = useState({ name: '', company: '', email: '', phone: '', products: '', qty: '', notes: '' });
  const [contactForm, setContactForm] = useState({ name: '', email: '', subject: '', message: '' });
  const [tab, setTab] = useState('quote');
  const [submitted, setSubmitted] = useState(null);
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);

  const submitQuote = async (e) => {
    e.preventDefault(); setLoading(true); setApiError(null);
    try {
      await axios.post(`${API}/public/inquiry`, {
        target_slug: slug || d.slug, target_type: 'supplier', inquiry_type: 'brief',
        name: quoteForm.name, company: quoteForm.company, email: quoteForm.email,
        phone: quoteForm.phone,
        notes: `Products: ${quoteForm.products} | Quantity: ${quoteForm.qty} | Notes: ${quoteForm.notes}`,
      });
      setSubmitted('quote');
    } catch { setApiError('Gönderim sırasında hata oluştu.'); } finally { setLoading(false); }
  };

  const submitContact = async (e) => {
    e.preventDefault(); setLoading(true); setApiError(null);
    try {
      await axios.post(`${API}/public/inquiry`, {
        target_slug: slug || d.slug, target_type: 'supplier', inquiry_type: 'contact',
        name: contactForm.name, email: contactForm.email,
        subject: contactForm.subject, message: contactForm.message,
      });
      setSubmitted('contact');
    } catch { setApiError('Gönderim sırasında hata oluştu.'); } finally { setLoading(false); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="space-y-4">
        <div className="bg-[#0B1E4D] rounded-2xl p-6 text-white">
          <h3 className="font-bold text-lg mb-4">Contact Details</h3>
          <div className="space-y-4">
            {[
              { Icon: Mail, label: 'Email', value: d.email },
              { Icon: Phone, label: 'Phone', value: d.phone },
              { Icon: Globe, label: 'Website', value: d.website },
              { Icon: MapPin, label: 'Location', value: d.location },
            ].map(({ Icon, label, value }) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-9 h-9 bg-white/10 rounded-xl flex items-center justify-center"><Icon className="w-4 h-4 text-white" /></div>
                <div><p className="text-xs text-white/60">{label}</p><p className="text-sm font-medium">{value}</p></div>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-blue-50 border border-blue-100 rounded-2xl p-4">
          <div className="flex items-center gap-2 mb-1">
            <Clock className="w-4 h-4 text-[#0B1E4D]" />
            <p className="font-semibold text-gray-900 text-sm">Fast Response</p>
          </div>
          <p className="text-xs text-gray-500">Average response: <strong className="text-[#0B1E4D]">{d.responseTime}</strong></p>
        </div>
      </div>

      <div className="lg:col-span-2">
        {submitted ? (
          <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-10 text-center">
            <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">{submitted === 'quote' ? 'Quote Request Sent!' : 'Message Sent!'}</h3>
            <p className="text-gray-500 text-sm mb-4">The team will respond within {d.responseTime}.</p>
            <button onClick={() => setSubmitted(null)} className="text-[#0B1E4D] text-sm font-semibold hover:underline">Send another</button>
          </div>
        ) : (
          <div className="bg-white border border-gray-100 rounded-2xl shadow-sm overflow-hidden">
            <div className="flex border-b border-gray-100">
              <button onClick={() => setTab('quote')} className={`flex-1 py-4 text-sm font-semibold transition-colors ${tab === 'quote' ? 'text-[#0B1E4D] border-b-2 border-[#0B1E4D]' : 'text-gray-500'}`}>Request a Quote</button>
              <button onClick={() => setTab('contact')} className={`flex-1 py-4 text-sm font-semibold transition-colors ${tab === 'contact' ? 'text-[#0B1E4D] border-b-2 border-[#0B1E4D]' : 'text-gray-500'}`}>Quick Contact</button>
            </div>

            {tab === 'quote' ? (
              <form onSubmit={submitQuote} className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Full Name *</label><Input value={quoteForm.name} onChange={e => setQuoteForm(p=>({...p,name:e.target.value}))} placeholder="John Smith" required className="h-10"/></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Company *</label><Input value={quoteForm.company} onChange={e => setQuoteForm(p=>({...p,company:e.target.value}))} placeholder="Your Company" required className="h-10"/></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Email *</label><Input type="email" value={quoteForm.email} onChange={e => setQuoteForm(p=>({...p,email:e.target.value}))} placeholder="john@company.com" required className="h-10"/></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Phone</label><Input value={quoteForm.phone} onChange={e => setQuoteForm(p=>({...p,phone:e.target.value}))} placeholder="+49 123 456 789" className="h-10"/></div>
                  <div className="sm:col-span-2"><label className="text-xs font-semibold text-gray-500 mb-1 block">Products / SKUs *</label><Input value={quoteForm.products} onChange={e => setQuoteForm(p=>({...p,products:e.target.value}))} placeholder="e.g. APX-300 x 3 kits, TFB-200 x 2 units" required className="h-10"/></div>
                  <div className="sm:col-span-2"><label className="text-xs font-semibold text-gray-500 mb-1 block">Total Quantity / Event Details</label><Input value={quoteForm.qty} onChange={e => setQuoteForm(p=>({...p,qty:e.target.value}))} placeholder="e.g. For 3 shows in 2025" className="h-10"/></div>
                </div>
                <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Additional Notes</label><Textarea value={quoteForm.notes} onChange={e => setQuoteForm(p=>({...p,notes:e.target.value}))} placeholder="Delivery address, custom branding requirements, etc." rows={3}/></div>
                <Button type="submit" data-testid="submit-quote-btn" disabled={loading} className="w-full bg-[#0B1E4D] hover:bg-[#163A7D] h-11 font-semibold">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Request Quote from {d.name}
                </Button>
                {apiError && <p className="text-red-500 text-sm text-center">{apiError}</p>}
              </form>
            ) : (
              <form onSubmit={submitContact} className="p-6 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Name *</label><Input value={contactForm.name} onChange={e => setContactForm(p=>({...p,name:e.target.value}))} placeholder="Your Name" required className="h-10"/></div>
                  <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Email *</label><Input type="email" value={contactForm.email} onChange={e => setContactForm(p=>({...p,email:e.target.value}))} placeholder="your@email.com" required className="h-10"/></div>
                  <div className="sm:col-span-2"><label className="text-xs font-semibold text-gray-500 mb-1 block">Subject</label><Input value={contactForm.subject} onChange={e => setContactForm(p=>({...p,subject:e.target.value}))} placeholder="e.g. Bulk order enquiry" className="h-10"/></div>
                </div>
                <div><label className="text-xs font-semibold text-gray-500 mb-1 block">Message *</label><Textarea value={contactForm.message} onChange={e => setContactForm(p=>({...p,message:e.target.value}))} placeholder="Your message..." rows={5} required/></div>
                <Button type="submit" data-testid="submit-supplier-contact-btn" disabled={loading} className="w-full bg-[#0B1E4D] hover:bg-[#163A7D] h-11 font-semibold">
                  {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
                  Send Message
                </Button>
                {apiError && <p className="text-red-500 text-sm text-center">{apiError}</p>}
              </form>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/* ─── MAIN ──────────────────────────────────────────────── */
const SupplierPublicProfile = () => {
  const { slug } = useParams();
  const [activeTab, setActiveTab] = useState('overview');
  const [apiData, setApiData] = useState(null);

  // Fetch real supplier data and merge with mock defaults
  useEffect(() => {
    if (!slug) return;
    axios.get(`${API}/public/supplier/${slug}`)
      .then(res => setApiData(res.data))
      .catch(() => { /* fall back to mock data silently */ });
  }, [slug]);

  // Merge real API data on top of mock data
  const d = { ...SUPPLIER_DATA };
  if (apiData?.org) {
    const o = apiData.org;
    if (o.display_name) d.name = o.display_name;
    if (o.city || o.country) d.location = [o.city, o.country].filter(Boolean).join(', ');
    if (o.email) d.email = o.email;
    if (o.phone) d.phone = o.phone;
    if (o.website) d.website = o.website;
    if (o.created_at) d.memberSince = new Date(o.created_at).getFullYear().toString();
  }
  if (apiData?.about) d.about = apiData.about;
  if (apiData?.tagline) d.tagline = apiData.tagline;
  if (apiData?.cover_image_url) d.coverImage = apiData.cover_image_url;
  if (apiData?.logo_url) d.logoUrl = apiData.logo_url;
  if (apiData?.catalog?.categories?.length > 0) d.categories = apiData.catalog.categories;
  // Map real coverage: countries and active_shows
  if (apiData?.coverage?.countries?.length > 0) {
    d.coverage.countries = apiData.coverage.countries.map(c => ({
      name: c.name || c, flag: c.flag || '🌍', primary: c.primary || false, cities: c.cities || []
    }));
  }
  if (apiData?.coverage?.active_shows?.length > 0) {
    d.active_shows = apiData.coverage.active_shows;
  }
  if (apiData?.coverage?.shipping_note) d.coverage.shippingNote = apiData.coverage.shipping_note;
  // Map real catalog items to products
  if (apiData?.catalog?.items?.length > 0) {
    d.products = apiData.catalog.items.map((item, idx) => ({
      id: item.id || `item_${idx}`,
      category: item.category || (apiData.catalog.categories?.[0] || 'General'),
      name: item.name || 'Product',
      sku: item.sku || `SKU-${idx + 1}`,
      description: item.description || item.notes || '',
      moq: item.moq || item.min_qty ? `${item.moq || item.min_qty} pcs` : '-',
      leadTime: item.lead_time || item.delivery_days ? `${item.lead_time || item.delivery_days} days` : '-',
      price: item.price ? `From ${item.price}` : (item.price_from ? `From ${item.price_from}` : '-'),
      image: item.image_url || `https://images.unsplash.com/photo-1558618666-fcd25c85cd64?q=80&w=600&auto=format&fit=crop`,
      tags: item.tags || [],
    }));
  }
  if (apiData?.certifications?.length > 0) {
    d.certifications = apiData.certifications.map(c => ({ ...c, icon: BadgeCheck }));
  }
  if (apiData?.coverage?.countries?.length > 0) {
    d.coverage = { countries: apiData.coverage.countries, shippingNote: apiData.coverage.shipping_note || d.coverage.shippingNote };
  }
  if (apiData?.team?.length > 0) {
    d.team = apiData.team.map((m) => ({
      name: m.name || 'Team Member',
      role: m.role || 'Team Member',
      bio: m.bio || '',
      avatar: m.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(m.name || 'TM')}&size=120&background=7c3aed&color=fff`,
    }));
  }
  if (apiData?.social) {
    d.linkedin = apiData.social.linkedin || '';
    d.website = apiData.social.website || d.website;
  }

  const renderTab = () => {
    switch (activeTab) {
      case 'overview':       return <OverviewTab d={d} />;
      case 'products':       return <ProductsTab d={d} />;
      case 'certifications': return <CertificationsTab d={d} />;
      case 'team':           return <TeamTab d={d} />;
      case 'coverage':       return <CoverageTab d={d} />;
      case 'reviews':        return <ReviewsTab d={d} />;
      case 'contact':        return <ContactTab d={d} />;
      default: return null;
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{d ? `${d.name} — Exhibition Supplier | ExpoStand` : 'Exhibition Supplier | ExpoStand'}</title>
        <meta name="description" content={d ? `${d.name}: ${d.tagline}. ${d.location ? `Based in ${d.location}.` : ''} ${d.categories?.length > 0 ? `Supplies: ${d.categories.slice(0,3).join(', ')}.` : ''} Get a quote today.` : 'Find verified exhibition suppliers on ExpoStand.'} />
        <meta property="og:title" content={d ? `${d.name} | ExpoStand` : 'Exhibition Supplier | ExpoStand'} />
        <meta property="og:description" content={d?.tagline || 'Professional exhibition supplier'} />
        <meta property="og:type" content="profile" />
      </Helmet>
      {/* Topbar */}
      <div className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-gray-700 hover:text-[#0B1E4D] transition-colors text-sm font-medium">
            <ArrowLeft className="w-4 h-4" />
            <span className="font-bold text-orange-500">EXPO</span><span className="font-bold">STAND</span>
          </Link>
          <button onClick={() => setActiveTab('contact')} className="bg-[#0B1E4D] hover:bg-[#163A7D] text-white text-sm px-4 py-2 rounded-lg font-semibold transition-colors">
            Get a Quote
          </button>
        </div>
      </div>

      {/* Hero */}
      <div className="relative">
        <div className="h-56 md:h-72 overflow-hidden">
          <img src={d.coverImage} alt="cover" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent via-[#0B1E4D]/20 to-[#0B1E4D]/65" />
        </div>
        <div className="max-w-6xl mx-auto px-4">
          <div className="relative -mt-16 pb-6">
            <div className="flex flex-col sm:flex-row sm:items-end gap-4">
              <div className="w-24 h-24 rounded-2xl bg-white border-4 border-white shadow-xl flex items-center justify-center flex-shrink-0 overflow-hidden">
                {d.logoUrl ? (
                  <img src={d.logoUrl} alt={d.name} className="w-full h-full object-contain p-2" />
                ) : (
                  <div className="w-16 h-16 rounded-xl bg-[#0B1E4D] flex items-center justify-center">
                    <Package className="w-8 h-8 text-white" />
                  </div>
                )}
              </div>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <h1 className="text-2xl md:text-3xl font-black text-white drop-shadow-lg" style={{textShadow:'0 2px 8px rgba(0,0,0,0.4)'}}>
                    {d.name}
                  </h1>
                  {d.verified && <span className="flex items-center gap-1 bg-emerald-500 text-white text-xs font-bold px-2.5 py-1 rounded-full"><Shield className="w-3 h-3"/>Verified</span>}
                  {d.premium && <span className="flex items-center gap-1 bg-amber-500 text-white text-xs font-bold px-2.5 py-1 rounded-full"><Award className="w-3 h-3"/>Premium</span>}
                </div>
                <p className="text-white/80 text-sm drop-shadow" style={{textShadow:'0 1px 4px rgba(0,0,0,0.5)'}}>{d.tagline}</p>
              </div>
            </div>
            <div className="mt-5 bg-white rounded-2xl shadow-lg border border-gray-100 p-4 flex flex-wrap items-center gap-x-6 gap-y-3">
              <div className="flex items-center gap-1.5"><Stars rating={d.rating} /><span className="font-bold text-gray-900 text-sm">{d.rating}</span><span className="text-gray-400 text-sm">({d.reviewCount} reviews)</span></div>
              <div className="flex items-center gap-1.5 text-sm text-gray-600"><MapPin className="w-4 h-4 text-gray-400"/>{d.location}</div>
              <div className="flex items-center gap-1.5 text-sm text-gray-600"><Clock className="w-4 h-4 text-gray-400"/>Responds {d.responseTime}</div>
              <div className="flex items-center gap-1.5 text-sm text-gray-600"><CheckCircle className="w-4 h-4 text-emerald-500"/>Member since {d.memberSince}</div>
              <div className="ml-auto flex items-center gap-2">
                <a href={`mailto:${d.email}`} className="w-8 h-8 bg-gray-100 hover:bg-blue-100 rounded-lg flex items-center justify-center transition-colors"><Mail className="w-4 h-4 text-gray-600"/></a>
                <a href={`tel:${d.phone}`} className="w-8 h-8 bg-gray-100 hover:bg-blue-100 rounded-lg flex items-center justify-center transition-colors"><Phone className="w-4 h-4 text-gray-600"/></a>
                <a href={`https://${d.website}`} target="_blank" rel="noreferrer" className="w-8 h-8 bg-gray-100 hover:bg-blue-100 rounded-lg flex items-center justify-center transition-colors"><Globe className="w-4 h-4 text-gray-600"/></a>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="sticky top-[57px] z-30 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex overflow-x-auto scrollbar-none">
            {TABS.map(tab => (
              <button key={tab.id} data-testid={`supplier-tab-${tab.id}`}
                onClick={() => setActiveTab(tab.id)}
                className={`px-5 py-3.5 text-sm font-semibold whitespace-nowrap border-b-2 transition-all flex-shrink-0 ${activeTab === tab.id ? 'border-[#0B1E4D] text-[#0B1E4D]' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}>
                {tab.label}
                {tab.id === 'contact' && <span className="ml-2 bg-[#0B1E4D] text-white text-xs px-1.5 py-0.5 rounded-full font-bold">RFQ</span>}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">{renderTab()}</div>

      <div className="bg-[#0B1E4D] text-white py-12 mt-8">
        <div className="max-w-6xl mx-auto px-4 text-center">
          <h2 className="text-2xl font-black mb-2">Need a quote from {d.name}?</h2>
          <p className="text-white/70 mb-6 text-sm">Fill in your requirements and receive a detailed quote within {d.responseTime}.</p>
          <button onClick={() => { setActiveTab('contact'); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 rounded-xl font-bold transition-colors">
            Get a Quote
          </button>
        </div>
      </div>
    </div>
  );
};

export default SupplierPublicProfile;
